import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { ContactEntityKind, MailerLiteConnection, MailerLiteConnectionFilter, MailerLiteEnsureWebhookResult } from "@domaddress/shared";

type Props = {
  open: boolean;
  onClose: () => void;
  /** List title shown in the dialog (e.g. "Vrijwilligers"). */
  listTitle: string;
  /** entity_kind for this list view, or null for "alle contacten" with optional filter. */
  entityKind: ContactEntityKind | null;
  /** Current list filters from ContactsPage. */
  filters: {
    search: string;
    group_id: string;
    city: string;
    country: string;
    entity_kind: string;
    group_activity: "all" | "none" | "with_role";
  };
  onSuccess?: (result: {
    connection: MailerLiteConnection;
    webhook: MailerLiteEnsureWebhookResult | null;
    push: { examined: number; synced: number; skipped: number; failed: number } | null;
  }) => void;
};

const checkboxLabelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "0.6rem",
  cursor: "pointer",
  lineHeight: 1.45,
};

const checkboxInputStyle: React.CSSProperties = {
  marginTop: "0.2rem",
  flexShrink: 0,
};

export default function MailerLiteConnectModal({
  open,
  onClose,
  listTitle,
  entityKind,
  filters,
  onSuccess,
}: Props) {
  const [groupName, setGroupName] = useState("");
  const [requireEmail, setRequireEmail] = useState(true);
  const [requireConsent, setRequireConsent] = useState(false);
  const [twoWay, setTwoWay] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mlEnabled, setMlEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setGroupName(listTitle);
    api
      .get<{ data: { enabled: boolean } }>("/api/mailerlite/status")
      .then((r) => setMlEnabled(r.data.data.enabled))
      .catch(() => setMlEnabled(false));
  }, [open, listTitle]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const filter: MailerLiteConnectionFilter = {
      search: filters.search.trim() || undefined,
      group_id: filters.group_id || undefined,
      city: filters.city.trim() || undefined,
      country: filters.country.trim() || undefined,
      entity_kind: filters.entity_kind || entityKind || undefined,
      group_activity: filters.group_activity !== "all" ? filters.group_activity : undefined,
      require_email: requireEmail,
      require_marketing_consent: requireConsent,
    };
    try {
      const res = await api.post<{
        data: {
          connection: MailerLiteConnection;
          webhook: MailerLiteEnsureWebhookResult | null;
          push: { examined: number; synced: number; skipped: number; failed: number } | null;
        };
      }>("/api/mailerlite/connections/create-group", {
        group_name: groupName.trim(),
        source_type: entityKind || filters.entity_kind ? "entity_kind" : "selection",
        source_key: entityKind || filters.entity_kind || null,
        filter,
        two_way: twoWay,
        ensure_webhook: true,
        push_members: true,
      });
      onSuccess?.({
        connection: res.data.data.connection,
        webhook: res.data.data.webhook,
        push: res.data.data.push,
      });
      onClose();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error ?? "Koppelen mislukt");
    } finally {
      setBusy(false);
    }
  }

  const hasActiveFilters =
    !!filters.search.trim() ||
    !!filters.group_id ||
    !!filters.city.trim() ||
    !!filters.country.trim() ||
    (!!filters.entity_kind && !entityKind) ||
    filters.group_activity !== "all";

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-labelledby="ml-connect-title"
        onClick={(ev) => ev.stopPropagation()}
        style={{ maxWidth: 520, width: "100%" }}
      >
        <div className="modal-header">
          <h2 id="ml-connect-title" style={{ margin: 0 }}>
            MailerLite koppelen
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Sluiten" disabled={busy}>
            ✕
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="modal-body">
          <p className="page-subtitle" style={{ margin: 0 }}>
            Maakt een nieuwe groep in MailerLite en koppelt deze aan <strong>{listTitle}</strong>
            {hasActiveFilters ? " (met de huidige filters)" : ""}. Leden worden daarna gesynchroniseerd.
          </p>

          {mlEnabled === false && (
            <p className="form-error" style={{ margin: 0 }}>
              MailerLite is niet geconfigureerd op de server (<code>MAILERLITE_API_TOKEN</code>).
            </p>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="ml-group-name">Naam in MailerLite</label>
            <input
              id="ml-group-name"
              value={groupName}
              onChange={(ev) => setGroupName(ev.target.value)}
              required
              maxLength={255}
            />
          </div>

          <div className="detail-section" style={{ margin: 0, padding: "14px 16px" }}>
            <h3 className="section-title" style={{ fontSize: "0.95rem", margin: "0 0 0.75rem" }}>
              Welke contacten meenemen?
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  style={checkboxInputStyle}
                  checked={requireEmail}
                  onChange={(ev) => setRequireEmail(ev.target.checked)}
                />
                <span>Alleen contacten met een e-mailadres (aanbevolen)</span>
              </label>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  style={checkboxInputStyle}
                  checked={requireConsent}
                  onChange={(ev) => setRequireConsent(ev.target.checked)}
                />
                <span>Alleen contacten met een e-maillijst-abonnement (marketingtoestemming)</span>
              </label>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  style={checkboxInputStyle}
                  checked={twoWay}
                  onChange={(ev) => setTwoWay(ev.target.checked)}
                />
                <span>
                  Tweerichtingssync via label (wijzigingen in MailerLite worden teruggezet als label op de kaart)
                </span>
              </label>
            </div>
          </div>

          {error && <p className="form-error" style={{ margin: 0 }}>{error}</p>}

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              Annuleren
            </button>
            <button type="submit" className="btn-primary" disabled={busy || mlEnabled === false}>
              {busy ? "Bezig…" : "Groep aanmaken en koppelen"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
